/**
 * ── ONE BUTTON OR TWO IS A BLOCK, NOT A FIELD (audit row 16) ──────────────────
 * Port of the monolith's `ECH_SWAP` / `__echSwapFor` / `__echSwapTo`
 * (index.html L10945–10954, L11145–11168) and the strip at L11145.
 *
 * "One or two buttons" reads like a setting, and the repo's hand-built `cta`
 * block treats it as one (`buttons: "one" | "two"`). SFMC does not: Layne ships
 * ECH_1CTA and ECH_2CTA as two separate Content Builder blocks with different
 * markup and different field keys. So the answer is to swap WHICH BLOCK sits in
 * the template, carrying the copy across a field map so the choice isn't punished
 * — type a label, change your mind, and the label follows you.
 *
 * `map` is FROM this block's field TO the sibling's. Anything unmapped falls back
 * to the sibling's own default; nothing is invented.
 */
import { blockDef } from "../email/blocks";
import { echByName } from "./echRegistry";
import type { DynBlock } from "./echDyn";
import { m2SwapFor } from "./m2Blocks";

export interface SwapDef {
  group: string;
  opts: [string, string][];
  /** target block name → { fromField: toField } */
  map: Record<string, Record<string, string>>;
  /** fields with the same key on both blocks carry (Milestone 2 families, the Grand Opening pairs) */
  carrySame?: boolean;
  /** offer only the members that are actually loaded (a block Layne has not added to a
   *  library yet is left out, and a pair with one member left shows no strip) */
  onlyRegistered?: boolean;
}

/** the Featured card's own fields, identical on both blocks — carried 1:1 on a swap */
const FEATURED_CARD_SELF: Record<string, string> = Object.fromEntries(
  ["PlanName", "PlanImage", "PlanImageAlt", "SqFt", "SqFtLabel", "Beds", "BedsLabel", "Baths", "BathsLabel", "Price"].map((k) => [k, k]),
);

/** monolith `ECH_SWAP` (L10943–10948), verbatim — plus the Featured pair (2026-09-10) */
export const ECH_SWAP: Record<string, SwapDef> = {
  ECH_2CTA: {
    group: "cta",
    opts: [["ECH_1CTA", "One button"], ["ECH_2CTA", "Two buttons"]],
    map: { ECH_1CTA: { btn1Label: "btnLabel", btn1Url: "btnUrl" } },
  },
  ECH_1CTA: {
    group: "cta",
    opts: [["ECH_1CTA", "One button"], ["ECH_2CTA", "Two buttons"]],
    map: { ECH_2CTA: { btnLabel: "btn1Label", btnUrl: "btn1Url" } },
  },
  /* ── THE FEATURED CARD IS THE SAME CHOICE (2026-09-10, Layne per Justin: "default
     featured cards should be the 1 CTA module; option to add 2nd CTA") ─────────────
     Same shape as the CTA row: two Content Builder blocks, one strip. The card's own
     fields share names on both blocks and carry 1:1; the button pair maps onto the
     single button and back. Anything without a home on the other side (button 2)
     falls to the sibling's default, as everywhere else in this table. */
  ECH_Featured1CTA: {
    group: "featured",
    opts: [["ECH_Featured1CTA", "One button"], ["ECH_Featured2CTA", "Two buttons"]],
    map: { ECH_Featured2CTA: { ...FEATURED_CARD_SELF, BtnLabel: "btn1Label", FloorplanUrl: "btn1Url" } },
  },
  ECH_Featured2CTA: {
    group: "featured",
    opts: [["ECH_Featured1CTA", "One button"], ["ECH_Featured2CTA", "Two buttons"]],
    map: { ECH_Featured1CTA: { ...FEATURED_CARD_SELF, btn1Label: "BtnLabel", btn1Url: "FloorplanUrl" } },
  },
  /* ── THE GRAND OPENING PAIRS ARE THE SAME CHOICE (2026-10-30, Layne: "on dev I can't
     switch between one and two buttons on either Grand Opening template") ────────────
     Both pairs were Milestone 2 families only (m2Blocks.M2_FAMILIES), and the Inspector
     reads those only when the block-palette test mode is on (?m2=1). Here they sit with
     the CTA row and the Featured card, so the strip shows on every host. Everything
     except the button shares its key on both blocks and carries (`carrySame`); the one
     button becomes button 1 and back. Button 2 falls to the sibling's default. */
  ECH_GOInvite1CTA: {
    group: "goinvite",
    opts: [["ECH_GOInvite1CTA", "One button"], ["ECH_GOInvite2CTA", "Two buttons"]],
    map: { ECH_GOInvite2CTA: { CtaLabel: "cta1Label", CtaUrl: "cta1Url" } },
    carrySame: true, onlyRegistered: true,
  },
  ECH_GOInvite2CTA: {
    group: "goinvite",
    opts: [["ECH_GOInvite1CTA", "One button"], ["ECH_GOInvite2CTA", "Two buttons"]],
    map: { ECH_GOInvite1CTA: { cta1Label: "CtaLabel", cta1Url: "CtaUrl" } },
    carrySame: true, onlyRegistered: true,
  },
  ECH_GOTY1CTA: {
    group: "goty",
    opts: [["ECH_GOTY1CTA", "One button"], ["ECH_GOTY2CTA", "Two buttons"]],
    map: { ECH_GOTY2CTA: { CtaLabel: "cta1Label", CtaUrl: "cta1Url" } },
    carrySame: true, onlyRegistered: true,
  },
  ECH_GOTY2CTA: {
    group: "goty",
    opts: [["ECH_GOTY1CTA", "One button"], ["ECH_GOTY2CTA", "Two buttons"]],
    map: { ECH_GOTY1CTA: { cta1Label: "CtaLabel", cta1Url: "CtaUrl" } },
    carrySame: true, onlyRegistered: true,
  },
};

export function echSwapFor(name: string): SwapDef | null {
  const sw = ECH_SWAP[String(name || "")] || null;
  if (!sw || !sw.onlyRegistered) return sw;
  const opts = sw.opts.filter(([n]) => n === name || !!echByName(n));
  return opts.length < 2 ? null : { ...sw, opts };
}

export interface SwapResult { type: string; data: Record<string, unknown> }

/**
 * monolith `__echSwapTo` (L11152–11168). Returns the new `{type, data}` instead
 * of mutating `m` — React owns block state here — and resolves the sibling
 * through `echByName` (the block we actually PULLED) rather than the id table,
 * which is the same precedence `echSfmcSeq` uses.
 *
 * Returns null when the sibling has not been registered yet, exactly as the
 * monolith returns false on `if(!nd)`. The caller shows the monolith's toast.
 */
export function echSwapTo(m: DynBlock, fromName: string, toName: string): SwapResult | null {
  try {
    if (!toName || toName === fromName) return null;
    const meta = echByName(toName);
    if (!meta) return null;
    const def = blockDef(meta.key);
    if (!def) return null;

    const sw: SwapDef | null = echSwapFor(fromName) || m2SwapFor(fromName);
    const map = (sw && sw.map[toName]) || {};
    const src = (m && m.data) || {};

    /* the sibling's own defaults first — including its `__html`, which is the
       whole point: the new block renders its OWN markup */
    const data: Record<string, unknown> = { ...(def.def() as Record<string, unknown>) };

    /* a family whose members share field keys (Main Image 500 / 600) keeps what was
       typed under the same key; a key the sibling does not declare is left behind */
    if (sw && sw.carrySame) {
      Object.keys(src).forEach((k) => {
        if (k.indexOf("__") === 0 || !(k in data)) return;
        const v = src[k];
        if (v != null && String(v).trim() !== "") data[k] = v;
      });
    }

    /* then the mapped copy, but only where there is copy to carry */
    Object.keys(map).forEach((from) => {
      const v = src[from];
      if (v != null && String(v).trim() !== "") data[map[from]] = v;
    });

    /* then block-level state that isn't content — mode, takeover list, level,
       layout. `__html` is excluded because it IS content: keeping the old block's
       markup would render the old block under the new block's field keys. */
    Object.keys(src).forEach((k) => {
      if (k.indexOf("__") === 0 && k !== "__html") data[k] = src[k];
    });

    /* ── A CLAIM FOLLOWS ITS FIELD ACROSS THE SWAP (2026-10-30, review of #438) ──────
       `__socOff` (fields the requester took over) and `__socFed` (what the last fill
       wrote) are keyed by FIELD. Copied verbatim they named the OLD block's keys, so a
       URL typed into `CtaUrl` arrived as `cta1Url` unclaimed and the next fill wrote
       over it. Each key goes through the same map as its value; a key the sibling does
       not declare is dropped. Same fix for the CTA row and the Featured pair. */
    const rekey = (k: string): string | null => {
      const t = map[k] || k;
      return t.indexOf("__") !== 0 && t in data ? t : null;
    };
    if (typeof src.__socOff === "string") {
      const keys = String(src.__socOff).split(",").map((k) => k.trim()).filter(Boolean)
        .map(rekey).filter((k): k is string => !!k);
      data.__socOff = Array.from(new Set(keys)).join(",");
    }
    if (typeof src.__socFed === "string" && src.__socFed) {
      try {
        const fed = JSON.parse(String(src.__socFed)) as Record<string, unknown>;
        const out: Record<string, unknown> = {};
        Object.keys(fed || {}).forEach((k) => { const t = rekey(k); if (t) out[t] = fed[k]; });
        data.__socFed = JSON.stringify(out);
      } catch { /* an unreadable record is left as it was */ }
    }

    return { type: meta.key, data };
  } catch { return null; }
}
